import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'

import { useDebounce } from 'commons/hooks/common'
import { ApiError } from 'commons/types'
import { Request } from 'commons/utils/request'
import { useStack } from 'ui/hooks'
import { useOutsideClick } from 'ui/hooks/useOutsideClick'

import { handlerServerError } from '@orbit/errors'

import { GlobalSearchResponse } from '../types/GlobalSearchResponse'

const find = (searchString: string) =>
  Request.post<GlobalSearchResponse>(Request.urls.bank.globalSearch, { searchString })

const minSearchLength = 3

interface UseGlobalSearchReturn {
  dialogRef: MutableRefObject<null>
  searchText: string
  handleChange: (value: string) => void
  isLoading: boolean
  clearSearch: () => void
  results: GlobalSearchResponse['result']
  hasResults: boolean
  isError: boolean
  error: unknown
  refetch: () => void
  isSearching: boolean
  canSearch: boolean
  hasQueried: boolean
}

export const useGlobalSearch = (onClose: () => void): UseGlobalSearchReturn => {
  const dialogRef = useRef(null)
  useStack(dialogRef)

  useOutsideClick(dialogRef, () => {
    onClose()
  })

  const queryClient = useQueryClient()

  const [searchText, setSearchText] = useState<string>('')
  const [hasQueried, setHasQueried] = useState<boolean>(false)
  const searchTextRef = useRef(searchText)
  const previousQueriesRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    searchTextRef.current = searchText
  }, [searchText])

  const debouncedRefetch = useDebounce(
    useCallback(
      (newValue: string) => {
        if (newValue.trim().length >= minSearchLength) {
          if (!previousQueriesRef.current.has(newValue)) {
            previousQueriesRef.current.add(newValue)
            refetch()
          } else {
            const cachedData = queryClient.getQueryData(['globalSearch', newValue])
            if (cachedData) {
              setHasQueried(true)
            } else {
              refetch()
            }
          }
        }
      },
      [queryClient]
    ),
    500
  )

  const handleChange = useCallback(
    (value: string) => {
      if (value.trim().length === 0) {
        clearSearch()
      } else {
        setSearchText(value.trimStart())
        debouncedRefetch(value.trimStart())
      }
    },
    [debouncedRefetch]
  )

  const { data, isLoading, error, refetch, isError } = useQuery(
    ['globalSearch', searchText],
    () => find(searchTextRef.current),
    {
      enabled: false,
      keepPreviousData: true,
      staleTime: 300000, // 5 minutes
      cacheTime: 300000, // 5 minutes
      retryOnMount: false,
      refetchOnMount: false,
      onSuccess: () => setHasQueried(true),
      onError: (e: ApiError) => handlerServerError(e)
    }
  )

  const clearSearch = async () =>
    await queryClient.resetQueries({ queryKey: ['globalSearch', searchText], exact: true }).then(() => {
      setSearchText('')
      setHasQueried(false)
    })

  return {
    dialogRef,

    searchText,
    handleChange,
    isLoading,
    clearSearch,

    results: data?.result || [],
    hasResults: !!data && data.result.length > 0,
    isError,
    error,
    refetch,

    isSearching: isLoading && searchText.trim().length >= minSearchLength,
    canSearch: searchText.trim().length >= minSearchLength,
    hasQueried
  }
}
